import { decomposeSeasonal } from './seasonalDecomposer';
/**
 * Compute whole weeks until the 1st of the next occurrence of `peakMonth`
 * (1-based) from `today`. Rolls to next year if this year's occurrence has
 * already passed. `inPrePeakWindow` is true when within 8 weeks of the peak.
 */
export function seasonalPeakProximity(peakMonth, today) {
    const year = today.getFullYear();
    // Build the peak date for this year (1st of peakMonth). If already past,
    // roll forward to next year.
    let peak = new Date(year, peakMonth - 1, 1);
    if (peak.getTime() < startOfDay(today).getTime()) {
        peak = new Date(year + 1, peakMonth - 1, 1);
    }
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    const diffMs = peak.getTime() - startOfDay(today).getTime();
    const weeksUntilPeak = Math.max(0, Math.round(diffMs / msPerWeek));
    return { weeksUntilPeak, inPrePeakWindow: weeksUntilPeak <= 8 };
}
/**
 * Promote an ABC segment letter up one tier for seasonal uplift:
 * "A.." stays "A..", "B.." → "A..", "C.." → "B..". The XYZ letter is preserved
 * (e.g. "BY" → "AY", "CZ" → "BZ").
 */
export function seasonalSegmentUplift(currentSegment) {
    if (currentSegment.length < 2)
        return currentSegment;
    const abc = currentSegment[0].toUpperCase();
    const rest = currentSegment.slice(1);
    const promoted = abc === 'A' ? 'A' : abc === 'B' ? 'A' : abc === 'C' ? 'B' : abc;
    return `${promoted}${rest}`;
}
function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
/**
 * Apply seasonal uplift to the segment map.
 *
 * For each seasonal SKU with ≥ 12 monthly periods, decompose the demand to
 * derive `seasonalStrength` and `peakMonth`. If strength > 0.4 AND the peak is
 * within the pre-peak window (≤ 8 weeks), promote the SKU's segment one tier
 * and add it to `upliftedSkuIds`.
 *
 * Returns a new segment map (the input classifications are not mutated) plus
 * the set of uplifted SKU ids.
 */
export function applySeasonalUplift(skus, classifications, demandRecords, today = new Date()) {
    const segments = new Map(classifications.map((c) => [c.skuId, c.segment]));
    const upliftedSkuIds = new Set();
    // Aggregate monthly demand per SKU
    const monthlyBySku = new Map();
    for (const rec of demandRecords) {
        const monthKey = rec.date.slice(0, 7); // YYYY-MM
        if (!monthlyBySku.has(rec.skuId))
            monthlyBySku.set(rec.skuId, new Map());
        const m = monthlyBySku.get(rec.skuId);
        m.set(monthKey, (m.get(monthKey) ?? 0) + rec.quantity);
    }
    for (const sku of skus) {
        if (!sku.isSeasonal)
            continue;
        const monthly = monthlyBySku.get(sku.id);
        if (!monthly)
            continue;
        const periodMap = monthly;
        if (periodMap.size < 12)
            continue;
        const sortedMonths = Array.from(periodMap.keys()).sort();
        const periods = sortedMonths.map((m) => ({
            periodStart: `${m}-01`,
            quantity: periodMap.get(m) ?? 0,
        }));
        const decomposition = decomposeSeasonal(sku.id, periods, 12);
        if (decomposition.seasonalStrength <= 0.4)
            continue;
        const proximity = seasonalPeakProximity(decomposition.peakMonth, today);
        if (!proximity.inPrePeakWindow)
            continue;
        const current = segments.get(sku.id);
        if (!current)
            continue;
        segments.set(sku.id, seasonalSegmentUplift(current));
        upliftedSkuIds.add(sku.id);
    }
    return { segments, upliftedSkuIds };
}
//# sourceMappingURL=seasonalUplift.js.map